import type { ScriptData } from '@/components/platform/types'
import {
  getWebClientExecFromScriptNode,
  hasExplicitWebClientExecAttr,
} from '@/common/utils/webClientExec'
import {
  getDialogClosedNReturnScriptNode,
  getDialogClosedNReturnScriptText,
  getDialogClosedScriptNode,
} from '@/common/utils/dialogClosedScriptMeta'
import type { ScriptClientImportPreflightOptions } from '@/services/parse/analyzeScriptClientImportDeps'
import { requiresMiddleForClientImportDeps } from '@/services/parse/analyzeScriptClientImportDeps'
import type { ActionContext } from './types'
import { extractActionTextValue, isUnsetActionTransferType } from './normalizeActionTransfer'

/** id из action.layout (поддержка `_text` и plain value после парсинга формы). */
export function resolveActionLayoutTextId(layout: unknown): string {
  if (!layout || typeof layout !== 'object') return ''
  const raw = (layout as { id?: unknown }).id
  if (raw == null) return ''
  if (typeof raw === 'object' && raw !== null && '_text' in (raw as object)) {
    return String((raw as { _text?: unknown })._text ?? '').trim()
  }
  return String(raw).trim()
}

/** level / position из action.layout. */
export function resolveActionLayoutLevelText(layout: unknown): string {
  if (!layout || typeof layout !== 'object') return ''
  const raw = (layout as { level?: unknown }).level
  if (raw == null) return ''
  if (typeof raw === 'object' && raw !== null && '_text' in (raw as object)) {
    return String((raw as { _text?: unknown })._text ?? '').trim()
  }
  return String(raw).trim()
}

export function resolveActionLayoutPositionText(layout: unknown): string {
  if (!layout || typeof layout !== 'object') return ''
  const raw = (layout as { position?: unknown }).position
  if (raw == null) return ''
  if (typeof raw === 'object' && raw !== null && '_text' in (raw as object)) {
    return String((raw as { _text?: unknown })._text ?? '').trim()
  }
  return String(raw).trim()
}

/** Тип transfer «не задан»: нет платформенного doAction по transferType. */
export function isNoPlatformTransferAction(
  context: Pick<ActionContext, 'transfer' | 'transferType'>,
): boolean {
  const transfer = String(extractActionTextValue(context.transfer) ?? '').trim()
  if (transfer !== '0' && transfer !== '') {
    return false
  }
  return isUnsetActionTransferType(context.transferType)
}

/** `<isContext>context</isContext>` или `<isContext>1</isContext>`. */
export function isActionMetadataContext(context: Pick<ActionContext, 'data'>): boolean {
  const isContextAction = context.data?.isContext?._text ?? context.data?.isContext
  const isContextValue = String(isContextAction ?? '').trim().toLowerCase()
  return isContextValue === 'context' || isContextValue === '1'
}

/** Тело скрипта из ScriptData или fallback (например context.onClick). */
export function getActionScriptBody(
  scriptData?: ScriptData,
  fallbackText?: string | null,
): string {
  const fromNode = scriptData?._text
  if (typeof fromNode === 'string') return fromNode
  return String(fallbackText ?? '')
}

/** Пустой onClick/chainCalled — middle не вызываем. */
export function isEmptyActionScript(
  scriptData?: ScriptData,
  fallbackText?: string | null,
): boolean {
  return getActionScriptBody(scriptData, fallbackText).trim() === ''
}

export function isClientOnlyUiMutationScript(script: string): boolean {
  const body = script.trim()
  if (!body) return false
  if (/\bimportClass\s*\(|\bimportPackage\s*\(|\bPackages\./.test(body)) return false
  return (
    /\ben\.dispatchVisibility\s*\(/.test(body) ||
    /\ben\.setVisible\s*\(/.test(body) ||
    /\ben\.getElement\s*\([^)]*\)\.setVisible\s*\(/.test(body)
  )
}

/** `foo(); bar();` — только вызовы идентификатора без аргументов. */
function extractNoArgCallNames(script: string): string[] | null {
  const statements = script
    .split(/[\n;]+/)
    .map((line) => line.trim())
    .filter(Boolean)
  if (statements.length === 0) return null
  const names: string[] = []
  for (const line of statements) {
    const match = /^([A-Za-z_$][\w$]*)\s*\(\s*\)$/.exec(line)
    if (!match) return null
    names.push(match[1])
  }
  return names
}

function extractNamedFunctionBody(functions: string, name: string): string | null {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`\\bfunction\\s+${escaped}\\s*\\(`)
  const match = re.exec(functions)
  if (!match) return null
  let i = match.index + match[0].length
  let parenDepth = 1
  while (i < functions.length && parenDepth > 0) {
    const ch = functions[i]
    if (ch === '(') parenDepth++
    else if (ch === ')') parenDepth--
    i++
  }
  while (i < functions.length && /\s/.test(functions[i])) i++
  if (functions[i] !== '{') return null
  i += 1
  const bodyStart = i
  let braceDepth = 1
  while (i < functions.length && braceDepth > 0) {
    const ch = functions[i]
    if (ch === '{') braceDepth++
    else if (ch === '}') braceDepth--
    if (braceDepth === 0) break
    i++
  }
  if (braceDepth !== 0) return null
  return functions.slice(bodyStart, i)
}

function isClientSafeFormFunctionBody(body: string): boolean {
  const trimmed = body.trim()
  if (!trimmed) return false
  if (/\bstorage\./.test(trimmed)) return false
  return isClientOnlyUiMutationScript(trimmed) || isClientOnlyRefillScript(trimmed)
}

/**
 * onChange вида `updateAddressVisibility();` — клиент, если helper из `<functions>`
 * только setVisible/refill (без Packages / storage). En.run prepend-ит functions.
 */
export function isClientSafeFormFunctionCall(
  script: string,
  formFunctions?: string | null,
): boolean {
  const functions = String(formFunctions ?? '').trim()
  if (!functions) return false
  const names = extractNoArgCallNames(script)
  if (!names || names.length === 0) return false
  return names.every((name) => {
    const helperBody = extractNamedFunctionBody(functions, name)
    return helperBody != null && isClientSafeFormFunctionBody(helperBody)
  })
}

/** onClick только с putFullData — на клиенте (корректный UniDataTable для FILEATTACHMENTS). */
function isPutFullDataOnlyScript(script: string): boolean {
  const body = script.trim()
  if (!body) return false
  if (/\bimportClass\s*\(|\bimportPackage\s*\(|\bPackages\./.test(body)) return false
  if (!/\ben\.putFullData\s*\(/.test(body)) return false
  const remainder = body
    .replace(/\ben\.putFullData\s*\(\s*["'][^"']+["']\s*\)/g, '')
    .replace(/;+/g, '')
    .trim()
  return remainder === ''
}

/**
 * Element onChange без Java/server-only: extractProtocolAndDomain, putFullData и/или putFormData.
 * Swing выполняет локально. На вебе POST executeScript + middle putFormData/refillElement
 * зависает (HTTP ждёт скрипт, скрипт ждёт WS js-request) — 2587 siteName → url;
 * 2627 hasSite → updateVisibility() / панели 4505, 4506, 4421.
 */
export function isClientSafeEditableTableOnChangeScript(
  script: string,
  importPreflight?: ScriptClientImportPreflightOptions,
): boolean {
  const body = script.trim()
  if (!body) return false

  // Before sanitize: bare importClass/Packages must stay on middle.
  if (/\bimportClass\s*\(|\bimportPackage\s*\(|\bPackages\./.test(body)) {
    return false
  }
  if (/\bstorage\./.test(body)) {
    return false
  }

  const usesExtract =
    /\$\(\s*['"]extractProtocolAndDomain['"]\s*\)/.test(body) ||
    /importer\.get\(\s*['"]extractProtocolAndDomain['"]\s*\)/.test(body)
  const usesPutFullData = /\ben\.putFullData\s*\(/.test(body)
  const usesPutFormData = /\ben\.putFormData\s*\(/.test(body)
  if (!usesExtract && !usesPutFullData && !usesPutFormData) {
    return false
  }

  return !requiresMiddleForClientImportDeps(
    body,
    withBrowserExtractProtocolAndDomainPreflight(importPreflight),
  )
}

/**
 * Куда слать element onChange: web_client_exec=true → клиент;
 * атрибута нет + client-safe → клиент; явный false → middle.
 * Helper из form `<functions>` (setVisible/getField, без Packages) — как onLoad.
 */
export function shouldExecuteElementOnChangeOnClient(
  scriptData?: ScriptData,
  scriptBody?: string,
  importPreflight?: ScriptClientImportPreflightOptions,
  formFunctions?: string | null,
): boolean {
  const body = getActionScriptBody(scriptData, scriptBody)
  if (!body.trim()) {
    return false
  }
  if (getWebClientExecFromScriptNode(scriptData)) {
    return true
  }
  if (hasExplicitWebClientExecAttr(scriptData)) {
    return false
  }
  if (isClientSafeEditableTableOnChangeScript(body, importPreflight)) {
    return true
  }
  if (isClientOnlyUiMutationScript(body) || isClientOnlyRefillScript(body)) {
    return true
  }
  return isClientSafeFormFunctionCall(body, formFunctions)
}

/**
 * Метаданные Swing: isContext и transfer=0 без платформенного transferType.
 * На web-маршрутизацию onClick не влияет.
 */
export function isImplicitContextClientAction(context: ActionContext): boolean {
  if (!isActionMetadataContext(context)) {
    return false
  }
  return isNoPlatformTransferAction(context)
}

function isClientOnlyRefillScript(script: string): boolean {
  const body = script.trim()
  if (!body) return false
  if (/\bimportClass\s*\(|\bimportPackage\s*\(|\bPackages\./.test(body)) return false

  const statements = body
    .split(/[\n;]+/)
    .map((line) => line.trim())
    .filter(Boolean)

  if (statements.length === 0) {
    return false
  }

  return statements.every(
    (line) =>
      /\ben\.refillElement\s*\(/.test(line) ||
      /\ben\.reloadElement\s*\(/.test(line) ||
      /\ben\.reloadForm\s*\(/.test(line) ||
      /\ben\.setVisible\s*\(/.test(line) ||
      /\ben\.dispatchVisibility\s*\(/.test(line) ||
      /\ben\.getElement\s*\([^)]*\)\.setVisible\s*\(/.test(line) ||
      /^\s*console\.log\s*\(/.test(line),
  )
}

/** onClick на клиенте только при web_client_exec на узле скрипта. */
export function shouldExecuteActionScriptOnClient(
  _context: ActionContext,
  scriptData?: ScriptData,
): boolean {
  return getWebClientExecFromScriptNode(scriptData)
}

/**
 * Meta import extractProtocolAndDomain is Java URI/IDN (serverOnly); En.$() has a browser port.
 * Same preflight as element onChange — do not send web_client_exec onClick to middle for it.
 */
export function withBrowserExtractProtocolAndDomainPreflight(
  importPreflight?: ScriptClientImportPreflightOptions,
): ScriptClientImportPreflightOptions {
  return {
    clientImportNames: [
      ...(importPreflight?.clientImportNames ?? []),
      'extractProtocolAndDomain',
    ],
    serverOnlyImportNames: (importPreflight?.serverOnlyImportNames ?? []).filter(
      (name) => name !== 'extractProtocolAndDomain',
    ),
  }
}

export function shouldExecuteOnClickOnClient(
  context: ActionContext,
  scriptData?: ScriptData,
  importPreflight?: ScriptClientImportPreflightOptions,
): boolean {
  const scriptBody = getActionScriptBody(scriptData, context.onClick)

  if (shouldExecuteActionScriptOnClient(context, scriptData)) {
    if (
      importPreflight &&
      requiresMiddleForClientImportDeps(
        scriptBody,
        withBrowserExtractProtocolAndDomainPreflight(importPreflight),
      )
    ) {
      return false
    }
    return true
  }
  if (hasExplicitWebClientExecAttr(scriptData)) {
    return false
  }
  if (isPutFullDataOnlyScript(scriptBody)) {
    return true
  }
  return isClientOnlyUiMutationScript(scriptBody)
}

export function shouldExecuteChainCalledOnClient(
  context: ActionContext,
  scriptData?: ScriptData,
): boolean {
  if (shouldExecuteActionScriptOnClient(context, scriptData)) {
    return true
  }
  if (hasExplicitWebClientExecAttr(scriptData)) {
    return false
  }
  const scriptBody = getActionScriptBody(scriptData, context.event?.chainCalled)
  return isClientOnlyRefillScript(scriptBody) || isClientOnlyUiMutationScript(scriptBody)
}

export function shouldExecuteDialogClosedOnClient(
  context: ActionContext,
  scriptData?: ScriptData,
): boolean {
  if (shouldExecuteActionScriptOnClient(context, scriptData)) {
    return true
  }
  if (hasExplicitWebClientExecAttr(scriptData)) {
    return false
  }
  // Parity with chainCalled: reloadForm/refill-only dialogClosed must run on client
  // (form 2610 action 28623 → en.reloadForm after email confirm dialog).
  const scriptBody = getActionScriptBody(scriptData, context.event?.dialogClosed)
  return isClientOnlyRefillScript(scriptBody) || isClientOnlyUiMutationScript(scriptBody)
}

/** Флаг client-exec для dialogCloseConfig при open NEW_DIALOG (ActionService / GlobalProvider). */
export function resolveOnCloseDialogWebClientExec(options: {
  actionItem?: Record<string, unknown> | null
  scriptText?: string | null
  responseFlag?: unknown
  scriptNode?: ScriptData
}): boolean {
  if (options.responseFlag === true) return true
  const scriptData =
    options.scriptNode ?? getDialogClosedScriptNode(options.actionItem ?? undefined)
  if (getWebClientExecFromScriptNode(scriptData)) {
    return true
  }
  if (hasExplicitWebClientExecAttr(scriptData)) {
    return false
  }
  const scriptBody = getActionScriptBody(scriptData, options.scriptText)
  return isClientOnlyRefillScript(scriptBody) || isClientOnlyUiMutationScript(scriptBody)
}

/** Флаг client-exec для `event.dialogClosedNReturn` в config слота диалога. */
export function resolveOnCloseDialogNReturnWebClientExec(options: {
  actionItem?: Record<string, unknown> | null
  scriptText?: string | null
  responseFlag?: unknown
  scriptNode?: ScriptData
}): boolean {
  if (options.responseFlag === true) return true
  const scriptData =
    options.scriptNode ?? getDialogClosedNReturnScriptNode(options.actionItem ?? undefined)
  if (getWebClientExecFromScriptNode(scriptData)) {
    return true
  }
  if (hasExplicitWebClientExecAttr(scriptData)) {
    return false
  }
  const scriptBody = getActionScriptBody(scriptData, options.scriptText)
  return isClientOnlyRefillScript(scriptBody) || isClientOnlyUiMutationScript(scriptBody)
}

/** Overrides config слота: текст NReturn + web_client_exec (не только из clientDialogCloseConfig). */
export function buildDialogClosedNReturnCloseOverrides(options: {
  actionItem?: Record<string, unknown> | null
  responseCfg?: Record<string, unknown> | null
  existingEvent?: unknown
  existingFlag?: unknown
}): { onCloseDialogNReturnEvent: string; onCloseDialogNReturnWebClientExec: boolean } | Record<string, never> {
  const fromAction = getDialogClosedNReturnScriptText(options.actionItem)
  const fromExisting =
    typeof options.existingEvent === 'string' && options.existingEvent.trim()
      ? options.existingEvent.trim()
      : undefined
  const fromResponse =
    typeof options.responseCfg?.onCloseDialogNReturnEvent === 'string' &&
    String(options.responseCfg.onCloseDialogNReturnEvent).trim()
      ? String(options.responseCfg.onCloseDialogNReturnEvent).trim()
      : undefined
  const text = fromAction ?? fromExisting ?? fromResponse
  if (!text) return {}
  return {
    onCloseDialogNReturnEvent: text,
    onCloseDialogNReturnWebClientExec: resolveOnCloseDialogNReturnWebClientExec({
      actionItem: options.actionItem,
      scriptText: text,
      responseFlag:
        options.existingFlag === true ||
        options.responseCfg?.onCloseDialogNReturnWebClientExec === true,
    }),
  }
}

/** Для core.service: routing dialogClosed — только явный web_client_exec на узле скрипта. */
export function shouldExecuteActionScriptOnClientFromActionItem(
  _actionItem: Record<string, unknown> | null | undefined,
  scriptNode?: ScriptData,
): boolean {
  return getWebClientExecFromScriptNode(scriptNode)
}
